import keys from "../config/keys";
import {BlobServiceClient} from "@azure/storage-blob";
import Api from "../Api/Api";

// module-scope caches so repeated calls reuse clients and quiz lookups
let __blobClientCache = null;
const __quizCache = new Map(); // key: quiz_db_ref -> data or null

// Simple concurrency limiter (no deps)
const withLimit = (limit) => {
  let active = 0;
  const queue = [];
  const next = () => {
    if (active >= limit || queue.length === 0) return;
    active++;
    const { fn, resolve, reject } = queue.shift();
    fn().then(resolve, reject).finally(() => {
      active--;
      next();
    });
  };
  return (fn) =>
    new Promise((resolve, reject) => {
      queue.push({ fn, resolve, reject });
      next();
    });
};

const getBlobClients = (sasUrl) => {
  if (__blobClientCache?.sasUrl === sasUrl) return __blobClientCache;
  const blobServiceClient = new BlobServiceClient(sasUrl);
  const containerClient = blobServiceClient.getContainerClient("certs");
  __blobClientCache = { sasUrl, blobServiceClient, containerClient };
  return __blobClientCache;
};

const time = () =>
  (typeof performance !== "undefined" && performance.now)
    ? performance.now()
    : Date.now();

export default {
  uploadCertNew: async (file, file_name, data, quiz) => {
    const blobSasUrl = process.env.REACT_APP_APP_INSTANCE === "MARATHON" ? keys.SAS_URL :  keys.SAS_URL_CREWBELT;
    const blobServiceClient = new BlobServiceClient(blobSasUrl);
    const containerClient = blobServiceClient.getContainerClient("certs");
    const blobClient = containerClient.getBlobClient(file_name);
    const blockBlobClient = blobClient.getBlockBlobClient();
    const result = await blockBlobClient.uploadBrowserData(file, {
      blockSize: 4 * 1024 * 1024,
      concurrency: 20,
      blobHTTPHeaders: {blobContentType: 'image/png'},
      metadata: {
        quiz: data.metadata.id,
        videoName: data.metadata.videoName || data.certification_title || "",
        today: data.todays_date,
        groupCode: data.metadata.isGroupCode ? 'yes' : 'no',
        issued: data.todays_date,
        quiz_db_ref: quiz?.entry_id ? quiz.entry_id?.toString() : ''
      },
      onProgress: ev => console.log(ev, "PROGRESS")
    });
    const url = containerClient.getBlockBlobClient(`${file_name}`).url.split('?')[0]
    // console.log(`Upload of file '${file_name}' completed}`,url, result);
    return url;
  },
  updateCert: async (file, file_name, data, targetBlobUrl) => {
    const blobSasUrl = process.env.REACT_APP_APP_INSTANCE === "MARATHON" ? keys.SAS_URL :  keys.SAS_URL_CREWBELT;
    const blobServiceClient = new BlobServiceClient(blobSasUrl);

    // If using a specific URL to update a blob
    let containerClient, blobName;
    if (targetBlobUrl) {
      const parsedUrl = new URL(targetBlobUrl);
      const pathParts = parsedUrl.pathname.split('/').filter(Boolean);
      const containerName = pathParts[0];
      blobName = decodeURIComponent(pathParts.slice(1).join('/')); // Decode once only
      containerClient = blobServiceClient.getContainerClient(containerName);
    } else {
      containerClient = blobServiceClient.getContainerClient("certs");
      blobName = decodeURIComponent(file_name); // ← Ensure it's not double-encoded
    }

    // If the original blob was a .pdf, upload to a .png path instead
    const oldBlobName = blobName;
    const isPdfBlob = blobName.toLowerCase().endsWith('.pdf');
    if (isPdfBlob) {
      blobName = blobName.replace(/\.pdf$/i, '.png');
    }

    const blobClient = containerClient.getBlobClient(blobName);
    const blockBlobClient = blobClient.getBlockBlobClient();

    const result = await blockBlobClient.uploadBrowserData(file, {
      blockSize: 4 * 1024 * 1024,
      concurrency: 20,
      blobHTTPHeaders: { blobContentType: 'image/png' },
      metadata: data,
      onProgress: ev => console.log(ev, "PROGRESS")
    });

    // Clean up the old .pdf blob now that the .png is uploaded
    if (isPdfBlob) {
      try {
        const oldBlobClient = containerClient.getBlobClient(oldBlobName);
        await oldBlobClient.delete();
      } catch (e) {
        console.warn(`Failed to delete old .pdf blob: ${oldBlobName}`, e);
      }
    }

    const url = blobClient.url.split('?')[0];
    // console.log(`Update of file '${blobName}' completed`, url, result);
    return url;
  },
  uploadImage: async (file, file_name) => {
    const blobSasUrl = process.env.REACT_APP_APP_INSTANCE === "MARATHON" ? keys.SAS_URL :  keys.SAS_URL_CREWBELT;
    const blobServiceClient = new BlobServiceClient(blobSasUrl);
    const containerClient = blobServiceClient.getContainerClient("profile-pictures");
    const blobClient = containerClient.getBlobClient(file_name);
    const blockBlobClient = blobClient.getBlockBlobClient();
    const result = await blockBlobClient.uploadBrowserData(file, {
      blockSize: 4 * 1024 * 1024,
      concurrency: 20,
      blobHTTPHeaders: {blobContentType: 'image/jpeg'},
      onProgress: ev => console.log(ev)
    });
    // console.log(`Upload of file '${file_name}' completed`);
  },
  fetchImage: async (id) => {
    try {
      const blobSasUrl = process.env.REACT_APP_APP_INSTANCE === "MARATHON" ? keys.SAS_URL :  keys.SAS_URL_CREWBELT;
      const blobServiceClient = new BlobServiceClient(blobSasUrl);
      const containerClient = blobServiceClient.getContainerClient("profile-pictures");
      const blobClient = containerClient.getBlobClient(id + '.jpg');
      const downloaded = await blobClient.download();
      const blob_file = await downloaded.blobBody;
      return URL.createObjectURL(blob_file);
    } catch (e) {
      return null;
    }
  },
  fetchSafetyDocs: async () => {
    try {
      // Taken Quizzes

      const blobSasUrl = process.env.REACT_APP_APP_INSTANCE === "MARATHON" ? keys.SAS_URL :  keys.SAS_URL_CREWBELT;
      const blobServiceClient = new BlobServiceClient(blobSasUrl);
      const containerClient = blobServiceClient.getContainerClient("safety-documents");
      let iter = containerClient.listBlobsFlat({ includeMetadata: true  });
      const documents = {}
      for await (const item of iter) {
        // Get Blob Client from name, to get the URL
        const tempBlockBlobClient = containerClient.getBlockBlobClient(item.name);
        if(item.metadata && item.metadata.cat) {
          if(documents[item.metadata.cat]) documents[item.metadata.cat].push({name: item.name, url: tempBlockBlobClient.url, metadata: item.metadata})
          else documents[item.metadata.cat] = [{name: item.name, url: tempBlockBlobClient.url, metadata: item.metadata}]
        }
      }
      return documents;
    } catch (e) {
      console.log(e);
      return null;
    }
  },
  fetchCertificates: async (id) => {
    const t0 = time();
    try {
      const blobSasUrl =
        process.env.REACT_APP_APP_INSTANCE === "MARATHON"
          ? keys.SAS_URL
          : keys.SAS_URL_CREWBELT;

      const { containerClient } = getBlobClients(blobSasUrl);

      // 1) List blobs (paged, larger page size)
      const listStart = time();
      const blobs = [];
      // Use byPage to control page size (reduces many small requests)
      const pages = containerClient
        .listBlobsFlat({ prefix: `${id}/`, includeMetadata: true })
        .byPage({ maxPageSize: 500 });

      for await (const page of pages) {
        for (const item of page.segment.blobItems) {
          blobs.push(item);
        }
      }
      const tList = time() - listStart;

      if (blobs.length === 0) {
        console.table({ blobs_found: 0, list_ms: Math.round(tList), total_ms: Math.round(time() - t0) });
        return [];
      }

      // 2) Collect unique quiz refs
      const collectStart = time();
      const needQuizRefs = new Set();
      for (const b of blobs) {
        const ref = b.metadata?.quiz_db_ref;
        if (ref) needQuizRefs.add(ref);
      }
      const uniqueRefs = [...needQuizRefs];
      const tCollect = time() - collectStart;

      // 3) Fetch quizzes in parallel with a concurrency cap
      const quizStart = time();
      const limit = withLimit(6); // tune: 4–8 is often good
      await Promise.all(
        uniqueRefs.map((ref) =>
          __quizCache.has(ref)
            ? Promise.resolve()
            : limit(async () => {
              try {
                const res = await Api.getQuizzes(ref);
                __quizCache.set(ref, res?.data ?? null);
              } catch (e) {
                console.error("getQuizzes failed for", ref, e);
                __quizCache.set(ref, null);
              }
            })
        )
      );
      const tQuizzes = time() - quizStart;

      // 4) Build result rows
      const rowsStart = time();
      const certificates = blobs.map((item) => {
        const block = containerClient.getBlockBlobClient(item.name); // cheap (no network)
        const ref = item.metadata?.quiz_db_ref;
        const quiz = ref ? __quizCache.get(ref) : undefined;

        return ref
          ? { name: item.name, url: block.url, metadata: item.metadata, quiz }
          : { name: item.name, url: block.url, metadata: item.metadata, manualUpload: true };
      });
      const tRows = time() - rowsStart;

      const total = time() - t0;
      console.table({
        blobs_found: blobs.length,
        unique_quiz_refs: uniqueRefs.length,
        list_ms: Math.round(tList),
        collect_ms: Math.round(tCollect),
        quiz_fetch_ms: Math.round(tQuizzes),
        build_rows_ms: Math.round(tRows),
        total_ms: Math.round(total),
      });

      return certificates;
    } catch (e) {
      console.log(e, "ERROR");
      return null;
    }
  },
  deleteCert: async (file) => {
    const blobSasUrl = process.env.REACT_APP_APP_INSTANCE === "MARATHON" ? keys.SAS_URL :  keys.SAS_URL_CREWBELT;
    const blobServiceClient = new BlobServiceClient(blobSasUrl);
    const containerClient = blobServiceClient.getContainerClient("certs");
    const deleteBlob = containerClient.deleteBlob(file)
    console.log(deleteBlob)
    return deleteBlob;
  },
  deleteSafetyDoc: async (file) => {
    const blobSasUrl = process.env.REACT_APP_APP_INSTANCE === "MARATHON" ? keys.SAS_URL :  keys.SAS_URL_CREWBELT;
    const blobServiceClient = new BlobServiceClient(blobSasUrl);
    const containerClient = blobServiceClient.getContainerClient("safety-documents");
    const deleteBlob = containerClient.deleteBlob(file)
    // console.log(deleteBlob)
    return deleteBlob;
  },
  uploadCert: async (file, file_name, meta) => {
    // console.log(meta, file_name, "AZURE")
    const blobSasUrl = process.env.REACT_APP_APP_INSTANCE === "MARATHON" ? keys.SAS_URL :  keys.SAS_URL_CREWBELT;
    const blobServiceClient = new BlobServiceClient(blobSasUrl);
    const containerClient = blobServiceClient.getContainerClient("certs");
    const blobClient = containerClient.getBlobClient(file_name);
    const blockBlobClient = blobClient.getBlockBlobClient();
    const result = await blockBlobClient.uploadBrowserData(file, {
      blobHTTPHeaders: {blobContentType: file.type},
      metadata: meta
    });
    // // console.log(`Upload of file '${file_name}' completed`);
    return result;
  },
  uploadSafetyDocuments: async (file, file_name, meta) => {
    // console.log(meta, file_name, "AZURE")
    const blobSasUrl = process.env.REACT_APP_APP_INSTANCE === "MARATHON" ? keys.SAS_URL :  keys.SAS_URL_CREWBELT;
    const blobServiceClient = new BlobServiceClient(blobSasUrl);
    const containerClient = blobServiceClient.getContainerClient("safety-documents");
    const blobClient = containerClient.getBlobClient(file_name);
    const blockBlobClient = blobClient.getBlockBlobClient();
    const result = await blockBlobClient.uploadBrowserData(file, {
      blobHTTPHeaders: {blobContentType: 'application/pdf'},
      metadata: meta
    });
    // console.log(`Upload of file '${file_name}' completed`);
    return result;
  },
}
